import React, { useState } from 'react';
import { S, Icon, BtnPrimary, BtnSecondary } from '../regureStyles';

const AMS360_FIELDS = [
  'PolicyNumber', 'Insured.FirstName', 'Insured.LastName', 'Insured.Email',
  'Insured.Phone', 'PolicyEffectiveDate', 'PolicyExpirationDate', 'AnnualPremium',
  'LineOfBusiness', 'CarrierName', 'AgencyCode', 'ProducerCode',
  'Insured.Address1', 'Insured.City', 'Insured.State', 'Insured.Zip',
  'PolicyType', 'TransactionType', 'BillingMethod', 'Commission',
];

const DEFAULT_MAPPINGS = [
  { regure: 'policyNumber', ams: 'PolicyNumber' },
  { regure: 'policyholder.firstName', ams: 'Insured.FirstName' },
  { regure: 'policyholder.lastName', ams: 'Insured.LastName' },
  { regure: 'policyholder.email', ams: 'Insured.Email' },
  { regure: 'policyholder.phone', ams: 'Insured.Phone' },
  { regure: 'effectiveDate', ams: 'PolicyEffectiveDate' },
  { regure: 'expirationDate', ams: 'PolicyExpirationDate' },
  { regure: 'annualPremium', ams: 'AnnualPremium' },
  { regure: 'lineOfBusiness', ams: 'LineOfBusiness' },
  { regure: 'carrier.name', ams: 'CarrierName' },
];

const SYNC_ENTITIES = [
  { name: 'Prospects', enabled: true },
  { name: 'Clients', enabled: true },
  { name: 'Policies', enabled: true },
  { name: 'Endorsements', enabled: false },
  { name: 'Claims', enabled: false },
  { name: 'Documents', enabled: true },
  { name: 'Carriers', enabled: false },
];

const AMS360Drawer = ({ connector, onClose, onSave }) => {
  const [form, setForm] = useState({
    accountId: '',
    agencyCode: '',
    apiUrl: 'https://api.vertafore.com/ams360/v1',
    authMethod: 'oauth2',
    syncFreq: '15min',
    syncDirection: 'bidirectional',
    conflictRes: 'manual',
  });
  const [entities, setEntities] = useState(SYNC_ENTITIES);
  const [mappings, setMappings] = useState(DEFAULT_MAPPINGS);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTestConnection = () => {
    setTesting(true);
    setTestResult(null);
    setTimeout(() => {
      setTesting(false);
      setTestResult('success');
    }, 2000);
  };

  const toggleEntity = (idx) => {
    setEntities(prev => prev.map((e, i) => i === idx ? { ...e, enabled: !e.enabled } : e));
  };

  const updateMapping = (idx, amsField) => {
    setMappings(prev => prev.map((m, i) => i === idx ? { ...m, ams: amsField } : m));
  };

  const isAms = connector.id === 'ams360';
  const title = isAms ? 'AMS360 (Vertafore) Configuration' : `${connector.name} Configuration`;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
        zIndex: 9000, animation: 'fadeIn 0.2s ease',
      }} />

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 640,
        background: '#fff', zIndex: 9001, display: 'flex', flexDirection: 'column',
        borderLeft: `4px solid ${S.burgundy}`,
        boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
        animation: 'slideIn 0.25s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: S.textPrimary }}>{title}</div>
            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
              {isAms ? 'Configure bi-directional sync with Vertafore AMS360' : `Configure ${connector.name} integration`}
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${S.borderLight}`,
            background: S.bgCard, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="x" color={S.textMuted} size={14} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>

          {/* Section A: Connection Details */}
          <SectionHeader title="Connection Details" icon="link" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            <Field label="Developer Account ID" value={form.accountId} onChange={v => setForm(f => ({ ...f, accountId: v }))} placeholder="e.g. VTF-12345" />
            <Field label="Agency Code" value={form.agencyCode} onChange={v => setForm(f => ({ ...f, agencyCode: v }))} placeholder="e.g. CIS-AZ-001" />
          </div>
          <Field label="API Endpoint URL" value={form.apiUrl} onChange={v => setForm(f => ({ ...f, apiUrl: v }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <div style={labelStyle}>Authentication Method</div>
              <select value={form.authMethod} onChange={e => setForm(f => ({ ...f, authMethod: e.target.value }))} style={inputStyle}>
                <option value="oauth2">OAuth 2.0</option>
                <option value="apikey">API Key</option>
                <option value="basic">Basic Auth</option>
              </select>
            </div>
            <div>
              <div style={labelStyle}>API Credentials</div>
              <button style={{ ...inputStyle, cursor: 'pointer', color: S.burgundy, fontWeight: 600, textAlign: 'center', background: S.bgCard }}>
                Set Credentials
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, marginBottom: 24 }}>
            <BtnPrimary onClick={handleTestConnection} disabled={testing} style={{ padding: '8px 16px', fontSize: 12 }}>
              {testing ? (
                <><span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} /> Testing...</>
              ) : 'Test Connection'}
            </BtnPrimary>
            {testResult === 'success' && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#15803d' }}>
                <Icon name="check-circle" color="#15803d" size={14} />
                Connection Successful
              </span>
            )}
          </div>

          {/* Section B: Sync Configuration */}
          <SectionHeader title="Sync Configuration" icon="refresh" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={labelStyle}>Sync Frequency</div>
              <select value={form.syncFreq} onChange={e => setForm(f => ({ ...f, syncFreq: e.target.value }))} style={inputStyle}>
                <option value="realtime">Real-time</option>
                <option value="15min">Every 15 minutes</option>
                <option value="1hr">Every hour</option>
                <option value="4hr">Every 4 hours</option>
                <option value="daily">Daily</option>
              </select>
            </div>
            <div>
              <div style={labelStyle}>Conflict Resolution</div>
              <select value={form.conflictRes} onChange={e => setForm(f => ({ ...f, conflictRes: e.target.value }))} style={inputStyle}>
                <option value="regure">Regure wins</option>
                <option value="ams360">AMS360 wins</option>
                <option value="manual">Manual review</option>
                <option value="timestamp">Most recent timestamp</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={labelStyle}>Sync Direction</div>
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                { value: 'regure_to_ams', label: 'Regure \u2192 AMS360' },
                { value: 'ams_to_regure', label: 'AMS360 \u2192 Regure' },
                { value: 'bidirectional', label: 'Bi-directional' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.textSecondary, cursor: 'pointer' }}>
                  <input type="radio" name="syncDir" value={opt.value} checked={form.syncDirection === opt.value}
                    onChange={() => setForm(f => ({ ...f, syncDirection: opt.value }))}
                    style={{ accentColor: S.burgundy }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Section C: Entity Mapping */}
          <SectionHeader title="Entity Mapping" icon="grid" />
          <div style={{ marginBottom: 24 }}>
            {entities.map((ent, idx) => (
              <div key={ent.name} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 0', borderBottom: idx < entities.length - 1 ? `1px solid ${S.borderLight}` : 'none',
              }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: S.textPrimary }}>{ent.name}</span>
                <ToggleSwitch checked={ent.enabled} onChange={() => toggleEntity(idx)} />
              </div>
            ))}
          </div>

          {/* Section D: Field Mapping (Policies) */}
          <SectionHeader title="Field Mapping — Policies" icon="file-text" />
          <div style={{
            border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12,
          }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 30px 1fr', gap: 0,
              background: S.bgCard, padding: '8px 14px',
              borderBottom: `1px solid ${S.borderLight}`,
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Regure Field</span>
              <span />
              <span style={{ fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>AMS360 Field</span>
            </div>
            {mappings.map((m, idx) => (
              <div key={idx} style={{
                display: 'grid', gridTemplateColumns: '1fr 30px 1fr', gap: 0, alignItems: 'center',
                padding: '8px 14px',
                borderBottom: idx < mappings.length - 1 ? `1px solid ${S.borderLight}` : 'none',
              }}>
                <code style={{ fontSize: 12, color: S.textPrimary, fontFamily: 'monospace' }}>{m.regure}</code>
                <span style={{ textAlign: 'center', color: S.textMuted, fontSize: 12 }}>{'\u2192'}</span>
                <select value={m.ams} onChange={e => updateMapping(idx, e.target.value)}
                  style={{ ...inputStyle, fontSize: 12, padding: '4px 8px', marginBottom: 0 }}>
                  {AMS360_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            ))}
          </div>
          <button style={{
            background: 'none', border: `1px dashed ${S.borderLight}`, borderRadius: 8,
            padding: '8px 16px', fontSize: 12, color: S.textMuted, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24,
          }}>
            <Icon name="plus" color={S.textMuted} size={12} />
            Add Custom Field Mapping
          </button>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${S.borderLight}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
        }}>
          <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
          <BtnPrimary onClick={onSave}>Save Configuration</BtnPrimary>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────
const labelStyle = {
  fontSize: 11, fontWeight: 600, color: S.textMuted,
  textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4,
};

const inputStyle = {
  width: '100%', padding: '8px 12px', fontSize: 13,
  border: `1px solid ${S.borderLight}`, borderRadius: 8,
  color: S.textPrimary, background: '#fff', outline: 'none',
  fontFamily: 'inherit', marginBottom: 8, boxSizing: 'border-box',
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={labelStyle}>{label}</div>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={inputStyle} />
  </div>
);

const SectionHeader = ({ title, icon }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 14, fontWeight: 700, color: S.textPrimary,
    marginBottom: 14, paddingBottom: 6,
    borderBottom: `2px solid ${S.burgundy}20`,
  }}>
    <Icon name={icon} color={S.burgundy} size={16} />
    {title}
  </div>
);

const ToggleSwitch = ({ checked, onChange }) => (
  <div onClick={onChange} style={{
    width: 38, height: 20, borderRadius: 10, cursor: 'pointer',
    background: checked ? S.burgundy : '#cbd5e1',
    position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 2,
      left: checked ? 20 : 2,
      transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    }} />
  </div>
);

export default AMS360Drawer;
